<script setup>
import { ref, computed, nextTick, onMounted, watch } from 'vue';
import { useStore } from 'dashboard/composables/store';
import { useRoute, useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useAccount } from 'dashboard/composables/useAccount';
import TaskColumn from 'dashboard/components-next/tasks/TaskColumn.vue';
import TaskDetailDialog from 'dashboard/components-next/tasks/TaskDetailDialog.vue';
import BoardSettingsDialog from 'dashboard/components-next/tasks/BoardSettingsDialog.vue';

const store = useStore();
const route = useRoute();
const router = useRouter();
const { t } = useI18n();
const { accountId } = useAccount();

const boardId = computed(() => Number(route.params.boardId));

const board = ref(null);
const columns = ref([]);
const selectedTask = ref(null);
const cardDialog = ref(null);
const boardDialog = ref(null);

const buildColumns = () => {
  const tasks = store.getters['tasks/getTasks'];
  const boardColumns = [...(board.value?.columns || [])].sort(
    (a, b) => a.position - b.position
  );
  columns.value = boardColumns.map(column => ({
    ...column,
    cards: tasks
      .filter(task => task.column_id === column.id)
      .sort((a, b) => a.position - b.position),
  }));
};

const loadBoard = async () => {
  if (!store.getters['tasks/getBoards'].length) {
    await store.dispatch('tasks/fetchBoards');
  }
  board.value = store.getters['tasks/getBoards'].find(
    item => item.id === boardId.value
  );
  if (!board.value) {
    router.replace({
      name: 'tasks_boards_index',
      params: { accountId: accountId.value },
    });
    return;
  }
  await store.dispatch('tasks/fetchTasks', boardId.value);
  buildColumns();
};

const totalCards = computed(() =>
  columns.value.reduce((sum, column) => sum + column.cards.length, 0)
);
const doneCount = computed(() => {
  const last = columns.value[columns.value.length - 1];
  return last ? last.cards.length : 0;
});
const progressPct = computed(() =>
  totalCards.value ? Math.round((doneCount.value / totalCards.value) * 100) : 0
);
const progressColor = computed(() => {
  const last = columns.value[columns.value.length - 1];
  return last?.color || '#10b981';
});
const progressLabel = computed(() => `${progressPct.value}%`);

const persistColumn = columnId => {
  const column = columns.value.find(item => item.id === columnId);
  if (!column) return;
  const positions = column.cards.map((card, index) => ({
    id: card.id,
    column_id: columnId,
    position: index,
  }));
  if (positions.length) store.dispatch('tasks/reorder', positions);
};

const findCard = id => {
  let found = null;
  columns.value.forEach(column => {
    const card = column.cards.find(item => item.id === id);
    if (card) found = card;
  });
  return found;
};

const onColumnChange = ({ columnId, event }) => {
  if (event.added) {
    // updateTask (não só reorder) pra registrar o "moveu de X para Y" no histórico.
    const card = event.added.element;
    card.column_id = columnId;
    store
      .dispatch('tasks/updateTask', {
        id: card.id,
        column_id: columnId,
        position: event.added.newIndex,
      })
      .then(updated => {
        const local = findCard(updated.id);
        if (local) local.activities = updated.activities;
      });
    persistColumn(columnId);
  } else if (event.moved || event.removed) {
    persistColumn(columnId);
  }
};

const addCard = async ({ columnId, title }) => {
  const column = columns.value.find(item => item.id === columnId);
  if (!column) return;
  const task = await store.dispatch('tasks/createTask', {
    task_board_id: boardId.value,
    column_id: columnId,
    title,
    position: column.cards.length,
  });
  column.cards.push(task);
};

const openCard = async task => {
  selectedTask.value = task;
  await nextTick();
  cardDialog.value?.open();
};

const openCardFromQuery = () => {
  const cardId = Number(route.query.card);
  if (!cardId) return;
  const card = findCard(cardId);
  if (card) openCard(card);
};

onMounted(async () => {
  await loadBoard();
  openCardFromQuery();
});

watch(() => route.query.card, openCardFromQuery);
watch(
  () => route.params.boardId,
  async newId => {
    if (!newId) return;
    await loadBoard();
    openCardFromQuery();
  }
);

const onCardClosed = () => {
  selectedTask.value = null;
  // tira ?card= da URL para o refresh não reabrir e o mesmo aviso poder disparar de novo
  if (route.query.card !== undefined) {
    router.replace({ query: { ...route.query, card: undefined } });
  }
};

const onTaskUpdated = updated => {
  const card = findCard(updated.id);
  if (card) Object.assign(card, updated);
};

const onTaskDeleted = id => {
  columns.value.forEach(column => {
    column.cards = column.cards.filter(card => card.id !== id);
  });
  selectedTask.value = null;
};

const openSettings = async () => {
  await nextTick();
  boardDialog.value?.open();
};

const QUICK_PALETTE = [
  '#64748b',
  '#3b82f6',
  '#6366f1',
  '#8b5cf6',
  '#f59e0b',
  '#10b981',
  '#f43f5e',
  '#06b6d4',
];

// Salva colunas + realoca cards de colunas removidas pra 1ª que sobrou.
const applyColumns = async newColumns => {
  const keptIds = newColumns.map(column => column.id);
  const removedIds = (board.value.columns || [])
    .map(column => column.id)
    .filter(id => !keptIds.includes(id));
  const payload = newColumns.map((column, index) => ({
    ...column,
    position: index,
  }));
  const updated = await store.dispatch('tasks/updateBoard', {
    id: boardId.value,
    columns: payload,
  });
  board.value = updated;
  if (removedIds.length) {
    const fallback = keptIds[0];
    const orphans = store.getters['tasks/getTasks'].filter(task =>
      removedIds.includes(task.column_id)
    );
    await Promise.all(
      orphans.map(task =>
        store.dispatch('tasks/updateTask', {
          id: task.id,
          column_id: fallback,
        })
      )
    );
  }
  buildColumns();
};

const renameColumn = ({ columnId, name }) => {
  applyColumns(
    (board.value.columns || []).map(column =>
      column.id === columnId ? { ...column, name } : column
    )
  );
};

const deleteColumn = columnId => {
  if ((board.value.columns || []).length <= 1) return;
  applyColumns((board.value.columns || []).filter(col => col.id !== columnId));
};

const quickAddColumn = () => {
  const current = board.value.columns || [];
  applyColumns([
    ...current,
    {
      id: crypto.randomUUID(),
      name: t('TASKS.NEW_COLUMN'),
      color: QUICK_PALETTE[current.length % QUICK_PALETTE.length],
      wip_limit: null,
    },
  ]);
};

// Ao salvar a pipeline: aplica o board novo e realoca cards de colunas
// removidas para a primeira coluna que sobrou (nada de card órfão).
const onBoardUpdated = async ({
  board: updatedBoard,
  removedIds,
  fallbackColumnId,
}) => {
  board.value = updatedBoard;
  if (removedIds.length && fallbackColumnId) {
    const orphans = store.getters['tasks/getTasks'].filter(task =>
      removedIds.includes(task.column_id)
    );
    await Promise.all(
      orphans.map(task =>
        store.dispatch('tasks/updateTask', {
          id: task.id,
          column_id: fallbackColumnId,
        })
      )
    );
  }
  buildColumns();
};
</script>

<template>
  <div class="flex flex-col w-full h-full overflow-hidden bg-n-background">
    <div class="flex items-center gap-3 px-6 py-3 border-b border-n-weak">
      <button
        class="flex items-center justify-center rounded-lg size-8 text-n-slate-11 hover:bg-n-alpha-2"
        @click="
          router.push({ name: 'tasks_boards_index', params: { accountId } })
        "
      >
        <span class="i-lucide-arrow-left size-4" />
      </button>

      <div class="flex flex-col min-w-0">
        <h1 class="text-lg font-medium truncate text-n-slate-12">
          {{ board?.name }}
        </h1>
        <span class="text-xs text-n-slate-11">
          {{ t('TASKS.CARD_COUNT', { count: totalCards }) }}
        </span>
      </div>

      <div class="flex items-center gap-2 ml-auto shrink-0">
        <div
          class="items-center hidden gap-2 sm:flex"
          :title="t('TASKS.DONE_RATIO')"
        >
          <div class="w-32 h-1.5 overflow-hidden rounded-full bg-n-alpha-2">
            <div
              class="h-full transition-all duration-500 rounded-full"
              :style="{
                width: `${progressPct}%`,
                backgroundColor: progressColor,
              }"
            />
          </div>
          <span class="text-xs font-medium tabular-nums text-n-slate-11">
            {{ progressLabel }}
          </span>
        </div>

        <button
          class="flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-lg text-n-slate-12 bg-n-alpha-2 hover:bg-n-alpha-3"
          @click="openSettings"
        >
          <span class="i-lucide-settings-2 size-4" />
          {{ t('TASKS.CONFIGURE_PIPELINE') }}
        </button>
      </div>
    </div>

    <div class="flex items-start flex-1 gap-4 p-6 overflow-x-auto group/board">
      <TaskColumn
        v-for="column in columns"
        :key="column.id"
        :column="column"
        :can-delete="columns.length > 1"
        @change="onColumnChange"
        @add-card="addCard"
        @open-card="openCard"
        @configure="openSettings"
        @rename="renameColumn"
        @remove="deleteColumn"
      />

      <button
        class="flex flex-col items-center justify-center w-12 gap-2 py-4 shrink-0 rounded-xl text-n-slate-10 border border-dashed border-n-weak hover:border-n-brand hover:text-n-slate-12 hover:bg-n-alpha-1"
        :title="t('TASKS.ADD_COLUMN')"
        @click="quickAddColumn"
      >
        <span class="i-lucide-plus size-4" />
      </button>
    </div>

    <TaskDetailDialog
      v-if="selectedTask"
      :key="selectedTask.id"
      ref="cardDialog"
      :task="selectedTask"
      @updated="onTaskUpdated"
      @deleted="onTaskDeleted"
      @close="onCardClosed"
    />

    <BoardSettingsDialog
      v-if="board"
      ref="boardDialog"
      :board="board"
      @updated="onBoardUpdated"
    />
  </div>
</template>
